import styles from './HeaderCell.module.scss';

import { Header } from '@tanstack/react-table';
import clsx from 'clsx';
import React, { type JSX, useState } from 'react';

import {
  Tooltip,
  Truncation,
} from '../../../../../../components/tooltip/Tooltip';
import { ColumnAdditionalData, ICssClasses } from '../../../../types';
import { SELECTION_COLUMN_ID } from '../../../../utils/makeRowsSelectable';
import { getCommonPinningStyles } from '../../../get-common-pinning-styles';
import { SeparatorV8 } from '../../../separator/SeparatorV8';
import { ITableCellProps, TableCell } from '../../../table-cell/TableCell';
import { HeaderCellContentV8 } from '../header-cell-content/HeaderCellContentV8';
import { HeaderCellWrapper } from '../header-cell-wrapper/HeaderCellWrapper';

export interface HeaderCellV8Props extends Truncation {
  header: Header<ColumnAdditionalData, unknown>;
  canResizeLast: boolean;
  canResizeCurrent: boolean;
  cellRef?: React.ForwardedRef<HTMLDivElement>;
  rootRef?: React.ForwardedRef<HTMLDivElement>;
  cellProps?: ITableCellProps;
  noSeperator: boolean;
  classes?: ICssClasses;
  columnDragIndicators?: Record<'before' | 'after', boolean>;
  style?: React.CSSProperties;
  hideTooltip?: boolean;
}

export const HeaderCellV8: React.FC<HeaderCellV8Props> = (props) => {
  const {
    header,
    rootRef,
    cellRef,
    canResizeCurrent,
    canResizeLast,
    noSeperator,
    cellProps,
    columnDragIndicators,
    style,
  } = props;

  let resizer: JSX.Element | null = null;
  if (header.id === SELECTION_COLUMN_ID || noSeperator) {
    resizer = null;
  } else if (canResizeCurrent && header.column.getCanResize()) {
    resizer = (
      <SeparatorV8
        className={props.classes?.headerSeperator}
        active={header.column.getIsResizing()}
        onMouseDown={header.getResizeHandler()}
        onTouchStart={header.getResizeHandler()}
        onDoubleClick={() => header.column.resetSize()}
      />
    );
  } else {
    resizer = (
      <SeparatorV8 disabled className={props.classes?.headerSeperator} />
    );
  }

  const noPaddingLeft = canResizeLast && !props.noSeperator;
  const noPaddingRight = canResizeCurrent && !props.noSeperator;

  const noBorderStyle = {
    border: header.id === 'PLACEHOLDER' ? 'unset' : undefined,
  };

  const [isTruncated, setIsTruncated] = useState(false);
  const handleLabelTruncation = (isTruncated: boolean) => {
    setIsTruncated(isTruncated);
  };

  const content = (
    <div
      key={header.id}
      ref={rootRef}
      className={clsx(
        styles.headerCellRoot,
        // col.className,
        props.classes?.headerCellRoot,
        {
          [styles.columnDragBefore]: columnDragIndicators?.before,
          [styles.columnDragAfter]: columnDragIndicators?.after,
        }
      )}
      style={{
        ...style,
        ...noBorderStyle,
        ...getCommonPinningStyles(header.column),
      }}
    >
      <HeaderCellWrapper>
        <TableCell
          className={styles['cell']}
          noPaddingLeft={noPaddingLeft}
          noPaddingRight={noPaddingRight}
          dataType={header.column.columnDef.dataType}
          alignment={header.column.columnDef.alignment}
          ref={cellRef}
          disableTooltip
          {...cellProps}
        >
          <HeaderCellContentV8
            col={header}
            classes={props.classes}
            onTruncation={handleLabelTruncation}
          />
        </TableCell>
        {resizer}
      </HeaderCellWrapper>
    </div>
  );

  if (!isTruncated) {
    return content;
  }
  return (
    <Tooltip
      title={getHeaderTitle(header.column.columnDef.header as string)}
      className={styles.tooltip}
      placement="bottom"
      enterNextDelay={1000}
      hideOnClickIfUnhoverd
    >
      {content}
    </Tooltip>
  );
};

export const getHeaderTitle = (value: React.ReactNode): React.ReactNode => {
  return typeof value !== 'function' ? value : '';
};
